/**
 * The checkout at phone width (web-sign-in-checkout D12; Gabriel 2026-10-21, "and
 * giving a green light for the phone checkout"; frames A to E of
 * docs/mockups/telify-web/phone-checkout.html): the Mini App's booking pages, one
 * step per screen. Each is the app's own screen (shared-client-screens D3):
 * FormStepScreen for registration and the studio's forms, PaymentStepScreen for
 * payment, ReviewStepScreen for the review, fed the same behavior halves as the
 * desktop checkout.
 *
 * Sign in is the one page the app lacks, drawn in the payment page's frame
 * (PaymentStepFrame); then registration and the studio's forms, then payment or
 * the review. Back steps as the app's does: one form back, then out to the
 * offering. Nothing here reaches Telegram's SDK (scripts/check-web-no-telegram.mjs).
 */
import type { ReactNode } from 'react';
import type { WebAppFlowContextResponse } from '@/api/types';
import type { BookingHeroProps } from '@/components/booking/BookingHero';
import { ActiveFlowConflictOverlay } from '@/components/flowConflict/ActiveFlowConflictOverlay';
import { formatCurrency } from '@/components/payment/formatCurrency';
import { getConfirmationView, getViewConfig } from '@/pages/ConfirmPage/getConfirmationView';
import type { PhoneCallProps } from '@/pages/ConfirmPage/PhoneCallRow';
import { BookingFailureScreen, ReviewStepLoading, ReviewStepScreen } from '@/pages/ConfirmPage/ReviewStepScreen';
import { FormStepPlaceholder, FormStepScreen, formFailure } from '@/pages/DetailsPage/FormStepScreen';
import { bookingHeroProps, phoneCallStep, whenLines } from '@/pages/booking/shared/bookingRules';
import { PaymentStepFrame } from '@/pages/PaymentPage/PaymentStepScreen';
import type { FormResponseInput } from '@/types/forms';
import { formatDuration } from '@/utils/formatDateTime';

import { useSiteSeries } from '../api/site';
import { getClientTimezone } from '../format';
import { useWebStepBack, type FormPage } from '../hooks/useWebForms';
import { useWebSavePhoneToCall } from '../hooks/useWebFlow';
import type { CheckoutRuntime } from '../pieces/checkout/useCheckout';
import { useFormsStep } from '../pieces/forms-step/useFormsStep';
import { HoldClockWarning, type HoldClockRuntime } from '../pieces/hold-clock';
import { HoldRecoveryView, type HoldRecoveryRuntime } from '../pieces/hold-recovery';
import { ConsentRow } from '../pieces/registration-step/RegistrationStepView';
import { useRegistrationStep } from '../pieces/registration-step/useRegistrationStep';
import { useReviewStep } from '../pieces/review-step/useReviewStep';
import { useSignInStep } from '../pieces/sign-in-step/useSignInStep';
import { SubmitErrorLine } from '../ui/FormSheet';
import { PhonePayment, TelegramIcon, usePhoneStepRails } from './PhoneBookingFrame';
import css from './PhoneCheckout.module.css';

// ── A: Sign in ──

/**
 * Sign in with Telegram in the app's payment page frame: the one page the app lacks, for the
 * checkout and for a signed-out visitor opening a booked page (row K11). The visitor stays on the page.
 */
export function PhoneSignIn({
  hero,
  onBack,
  title,
  total,
  holdNote,
}: {
  hero: BookingHeroProps;
  onBack: () => void;
  title?: string;
  total?: { readonly label: string; readonly value: string } | null;
  /** Under the button: what signing in holds. */
  holdNote?: string;
}) {
  const { state, actions } = useSignInStep({});
  usePhoneStepRails();
  const pending = state.attempt === 'pending';
  return (
    <PaymentStepFrame
      hero={hero}
      onBack={onBack}
      title={title}
      total={total}
      bar={{
        label: (
          <>
            <TelegramIcon />
            Sign in with Telegram
          </>
        ),
        className: css.tgBtn,
        disabled: !state.ready || pending,
        onPress: actions.signIn,
        note: holdNote ? <p className={css.holdNote}>{holdNote}</p> : null,
      }}
    >
      {state.welcomeBack && (
        <p className={css.welcome} role="status">
          Welcome back. Your pick is still here.
        </p>
      )}
      <p className={css.lead}>Your booking, reminders and messages from {state.studioName} arrive in Telegram.</p>
      {(state.attempt === 'error' || state.attempt === 'unavailable') && (
        <p className={css.error} role="alert">
          {state.attempt === 'unavailable' ? 'Sign in is not open at this studio yet.' : 'Sign in did not go through. Please try again.'}
        </p>
      )}
      <p className={css.noTg}>
        <b>No Telegram yet?</b> Get it on the{' '}
        <a href={state.storeLinks.appStore} target="_blank" rel="noopener noreferrer" onClick={() => actions.leftToGetTelegram('appStore')}>
          App Store
        </a>{' '}
        or{' '}
        <a href={state.storeLinks.googlePlay} target="_blank" rel="noopener noreferrer" onClick={() => actions.leftToGetTelegram('googlePlay')}>
          Google Play
        </a>
        , then come back here to finish.
      </p>
      {actions.signInAsTestAccount && (
        <button type="button" className={css.testAccount} disabled={pending} onClick={actions.signInAsTestAccount}>
          Sign in as test account
        </button>
      )}
    </PaymentStepFrame>
  );
}

function SignInScreen({ checkout, hero }: { checkout: CheckoutRuntime; hero: BookingHeroProps }) {
  const { summary } = checkout.state;
  return (
    <PhoneSignIn
      hero={hero}
      onBack={checkout.actions.back}
      title={checkout.state.title}
      total={summary && { label: 'Total', value: summary.amount != null ? formatCurrency(summary.amount) : 'Free' }}
      holdNote={checkout.state.kind === 'event' ? 'Your spot is held once you sign in.' : 'Your time is held once you sign in.'}
    />
  );
}

// ── B, C: registration and the studio's forms (FormStepScreen) ──

function FormScreen({
  page,
  hero,
  until,
  onSubmit,
  beforeButton,
}: {
  page: FormPage;
  hero: BookingHeroProps;
  until: string | null;
  onSubmit: (responses: FormResponseInput[]) => void;
  beforeButton?: ReactNode;
}) {
  const { scrollRef } = usePhoneStepRails();
  // A read that failed says so with Go Back (row R3); a form still loading says so (row R4).
  const failure = formFailure({ isError: page.formsError != null, errorMessage: page.formsError }, page.formFailed, page.back);
  if (failure) return <FormStepPlaceholder state={failure} />;
  if (!page.form) return <FormStepPlaceholder state={{ kind: 'loading', text: 'Loading form...' }} />;
  return (
    <FormStepScreen
      hero={hero}
      onBack={page.back}
      backDisabled={page.backPending}
      scrollRef={scrollRef}
      form={page.form}
      index={page.index}
      total={page.total}
      reserved={until}
      paymentAhead={page.paymentAhead}
      submitting={page.submitting}
      serverErrors={page.fieldErrors}
      onSubmit={onSubmit}
      onSave={page.save}
      beforeButton={
        page.submitError && !page.submitting ? (
          <>
            {beforeButton}
            <SubmitErrorLine text={page.submitError} />
          </>
        ) : beforeButton
      }
    />
  );
}

interface HeldScreenProps {
  flowToken: string;
  flow: WebAppFlowContextResponse;
  offering: { name: string; path: string } | null;
  hero: BookingHeroProps;
  until: string | null;
}

function RegistrationScreen({ flowToken, flow, offering, hero, until }: HeldScreenProps) {
  const rt = useRegistrationStep({ flowToken, flowContext: flow, offering });
  const consentRow = rt.page.form?.privacyConsentRequired ? <ConsentRow state={rt.state} actions={rt.actions} /> : null;
  return <FormScreen page={rt.page} hero={hero} until={until} onSubmit={rt.submit} beforeButton={consentRow} />;
}

function FormsScreen({ flowToken, flow, offering, hero, until }: HeldScreenProps) {
  const rt = useFormsStep({ flowToken, flowContext: flow, offering });
  return <FormScreen page={rt.page} hero={hero} until={until} onSubmit={rt.submit} />;
}

// ── D: payment (PaymentStepScreen) ──

function PaymentScreen({ flowToken, flow, offering, hero, until, amount }: HeldScreenProps & { amount: number }) {
  const stepBack = useWebStepBack(flowToken, flow, offering?.path ?? null);
  const phoneCall = usePhoneCallOf(flowToken, flow);
  return (
    <PhonePayment
      entry={{ kind: 'flow', flowToken }}
      amount={amount}
      hero={hero}
      until={until}
      onBack={stepBack.back}
      backDisabled={stepBack.pending}
      phoneCall={phoneCall}
    />
  );
}

/** A by-phone service's call line on the website session (service-by-phone D5); null for any other. */
function usePhoneCallOf(flowToken: string, flow: WebAppFlowContextResponse): PhoneCallProps | null {
  const save = useWebSavePhoneToCall(flowToken);
  const step = phoneCallStep(flow.context.service?.phoneCall);
  return step && { step, save };
}

// ── E: the review (ReviewStepScreen) for pay at the session, free and requests ──

function ReviewScreen({ flowToken, flow, offering, hero, until, checkout }: HeldScreenProps & { checkout: CheckoutRuntime }) {
  const stepBack = useWebStepBack(flowToken, flow, offering?.path ?? null);
  const { state, actions, failure } = useReviewStep({ flowToken, flowContext: flow, onBack: stepBack.back });
  // The app's rails; the keyboard rail serves a by-phone service's number to call (as the app's).
  const { scrollRef } = usePhoneStepRails();
  const phoneCall = usePhoneCallOf(flowToken, flow);
  const view = getViewConfig(getConfirmationView(flow));
  const summary = checkout.state.summary;
  // A series lists every date in its own zone (row V6), as the app's review does.
  const series = useSiteSeries(flow.flow.selectedSeriesId ?? undefined).data;
  // A booking that did not go through takes the screen (row V7).
  if (failure) return <BookingFailureScreen failure={failure} onRetry={actions.retry} onBack={actions.goBack} />;
  return (
    <ReviewStepScreen
      hero={hero}
      onBack={stepBack.back}
      backDisabled={stepBack.pending || state.confirming}
      scrollRef={scrollRef}
      title={view.title}
      subtitle={view.subtitle}
      reserved={until}
      when={summary?.startsAt ? whenLines(summary.startsAt, getClientTimezone(), series ? { dates: series.dates, timezone: series.timezone } : null) : []}
      // The app's length, even when the offering's is not known (row V5).
      duration={formatDuration(flow.context.service?.duration ?? 60)}
      location={summary?.location ? { virtual: summary.online, text: summary.location } : null}
      phoneCall={phoneCall}
      price={state.price}
      note={state.note}
      confirm={{
        label: state.confirmLabel,
        kind: state.request ? 'submit' : 'confirm',
        onPress: actions.confirm,
        busy: state.confirming,
        disabled: state.confirming || stepBack.pending,
      }}
    />
  );
}

// ── The checkout ──

export interface PhoneCheckoutProps {
  readonly checkout: CheckoutRuntime;
  readonly flowToken: string | null;
  readonly flow: WebAppFlowContextResponse | undefined;
  readonly clock: HoldClockRuntime;
  readonly recovery: HoldRecoveryRuntime;
  /** Counts each time the time was held again; the card form starts over for the new hold. */
  readonly holdAttempt: number;
}

export function PhoneCheckout({ checkout, flowToken, flow, clock, recovery, holdAttempt }: PhoneCheckoutProps) {
  const { state, actions } = checkout;
  const { summary, back } = state;
  const offering = back && summary ? { name: summary.name, path: back.href } : null;

  // The app's booking photo header: the service's zoom once the flow is held, no provider line
  // without one, and "Your booking" on the form and payment steps (rows R6, R7, R8). Only the
  // sign-in step, which the app lacks, and the review of a request read "Your request".
  const heroOf = (request: boolean): BookingHeroProps =>
    bookingHeroProps({
      photo: summary?.photo ? { ...summary.photo, scale: flow?.context.service?.focalPointScale ?? null } : null,
      name: summary?.name,
      provider: summary?.provider ?? null,
      startsAt: summary?.startsAt,
      tz: getClientTimezone(),
      request,
    });
  const hero = heroOf(state.request);

  if (state.session === 'signedOut') return <SignInScreen checkout={checkout} hero={hero} />;

  if (state.hold === 'taken' || state.hold === 'failed') {
    return (
      <PaymentStepFrame hero={hero} onBack={actions.back} title={state.title} bar={back && { label: 'Pick another time', onPress: actions.back }}>
        {state.problem && (
          <p className={css.error} role="alert">
            {state.problem}
          </p>
        )}
      </PaymentStepFrame>
    );
  }

  const step = flow?.flow.currentStep;
  const service = flow?.context.service;
  let screen: ReactNode;
  if (!flowToken || !flow || !service) {
    // The app's words while the booking is read (row R4).
    screen = <ReviewStepLoading />;
  } else {
    const held: HeldScreenProps = { flowToken, flow, offering, hero: heroOf(false), until: clock.state.until };
    if (step === 'REGISTRATION') screen = <RegistrationScreen key="registration" {...held} />;
    else if (step === 'CUSTOM_FORMS') screen = <FormsScreen key="forms" {...held} />;
    else if (!service.requiresApproval && (service.paymentRequirement === 'FULL_PAYMENT' || service.paymentRequirement === 'DEPOSIT')) {
      // The full price, as the app's payment page: a deposit is refused there (row P2).
      screen = <PaymentScreen key={`payment-${holdAttempt}`} {...held} amount={service.price ?? 0} />;
    } else screen = <ReviewScreen key="review" {...held} hero={hero} checkout={checkout} />;
  }

  return (
    <>
      {screen}
      <HoldClockWarning {...clock} />
      <HoldRecoveryView {...recovery} />
      {state.otherBooking && (
        <ActiveFlowConflictOverlay
          card={state.otherBooking}
          resolving={state.otherBooking.cancelling}
          onContinue={actions.continueOther}
          onStartOver={actions.startOver}
        />
      )}
    </>
  );
}
